import jsPDF from "jspdf";
import logoBase64 from "../../assets/logoBase64";
import autoTable from "jspdf-autotable";
import { RankingResult, ResultService } from "../../services/resultService";
import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Section } from "../../types/section/section";
import { Concorrente } from "../../types/concorrenti/concorrente";
import { GaraService } from "../../services/garaService";

interface ClassificaTabProps {
  sections: Section[];
  concorrenti: Concorrente[];
  competitionName: string;
  competitionDate?: string;
}

interface ClassificaEntry {
  concorrente: Concorrente;
  position: number;
  trote: number;
  punteggio: number;
}

type ClassifichePerSettore = {
  [sectionId: number]: ClassificaEntry[];
};

interface Coppia {
  groupId: number;
  competitors: Concorrente[];
}

const ClassificaCoppie: React.FC<ClassificaTabProps> = ({
  sections,
  concorrenti,
  competitionName,
  competitionDate,
}) => {
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(
    null
  );
  const [classifiche, setClassifiche] = useState<ClassifichePerSettore>({});
  const [coppie, setCoppie] = useState<Coppia[]>([]);
  const [availableCoppie, setAvailableCoppie] = useState<Coppia[]>([]);

  useEffect(() => {
    const fetchRanking = async () => {
      if (!sections || sections.length === 0) return;
      const competitionId = sections[0]?.competitionId;
      if (!competitionId) return;
      try {
        const results = await ResultService.getRanking(competitionId);
        const classificheFromApi: ClassifichePerSettore = {};
        // Raggruppa per sectionId e groupId
        sections.forEach((section) => {
          const sectionResults = results.filter(
            (r) => r.sectionId === section.id
          );
          const groupMap: { [groupId: number]: RankingResult[] } = {};
          sectionResults.forEach((item) => {
            if (item.groupId === undefined) return;
            if (!groupMap[item.groupId]) groupMap[item.groupId] = [];
            groupMap[item.groupId].push(item);
          });

          classificheFromApi[section.id] = Object.values(groupMap)
            .map((groupItems) => {
              // Aggrega i dati della coppia
              const position = Math.min(...groupItems.map((i) => i.position));
              const trote = groupItems.reduce(
                (sum, i) => sum + (i.totalFishCount ?? 0),
                0
              );
               const punteggio = groupItems[0]?.score ?? 0;
              // Nome completo accoppiato: "Cognome Nome & Cognome Nome ..."
              const nomiCompleti = groupItems.map(i => `${i.lastName} ${i.firstName}`).join(" & ");
              const concorrente: Concorrente = {
                id: groupItems[0].groupId,
                firstName: nomiCompleti,
                lastName: "",
              } as Concorrente;
              return { concorrente, position, trote, punteggio };
            })
            .sort((a, b) => a.position - b.position);
        });
        setClassifiche(classificheFromApi);
      } catch (err) {
        console.error("Errore nel recupero della classifica:", err);
      }
    };
    const fetchCoppie = async () => {
      if (!sections || sections.length === 0) return;
      const competitionId = sections[0]?.competitionId;
      if (!competitionId) return;
      try {
        const gruppi = await GaraService.getGroups(competitionId);
        setCoppie(gruppi);
        setAvailableCoppie(gruppi);
      } catch (err) {
        console.error("Errore nel recupero delle coppie:", err);
      }
    };
    fetchRanking();
    fetchCoppie();
  }, [sections, concorrenti]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleRemoveFromClassifica = (
    sectionId: number,
    concorrenteId: number
  ) => {
    setClassifiche((prev) => {
      const prevClassifica = prev[sectionId] || [];
      const newClassifica = prevClassifica
        .filter((e) => e.concorrente.id !== concorrenteId)
        .map((e, idx) => ({ ...e, position: idx + 1 }));
      const competitionId = sections[0]?.competitionId || 0;
      ResultService.saveRanking({
        competitionId,
        sectionId,
        ranking: newClassifica.map((entry) => ({
          concorrenteId: entry.concorrente.id,
          position: entry.position,
          catches: entry.trote,
          score: entry.punteggio,
        })),
      }).catch((err) => {
        setSaveMessage(
          "Errore nel salvataggio della classifica dopo la rimozione."
        );
        console.error("Errore salvataggio classifica:", err);
      });
      return {
        ...prev,
        [sectionId]: newClassifica,
      };
    });
    if (selectedSectionId === sectionId) {
      const coppiaRimossa = coppie.find((g) => g.groupId === concorrenteId);
      if (coppiaRimossa) {
        setAvailableCoppie((prev) => [...prev, coppiaRimossa]);
      }
    }
  };

  const handleSaveAllRankings = async () => {
    setSaving(true);
    setSaveMessage(null);
    const competitionId = sections[0]?.competitionId || 0;
    // Preparo tutte le richieste per tutti i settori
    const requests = sections.flatMap((section) => {
      const classifica = classifiche[section.id] || [];
      return classifica.map((entry) => ({
        competitionId,
        sectionId: section.id,
        groupId: entry.concorrente.id,
        competitorIds:
          coppie
            .find((g) => g.groupId === entry.concorrente.id)
            ?.competitors.map((c) => c.id) || [],
        fishCount: entry.trote,
        score: entry.punteggio,
        position: entry.position,
      }));
    });
    try {
      await ResultService.saveGroupRanking(competitionId, requests);
      setSaveMessage("Tutte le classifiche sono state salvate con successo!");
    } catch (err) {
      setSaveMessage("Errore nel salvataggio di una o più classifiche");
      console.error(err);
    }
    setSaving(false);
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sectionId = Number(e.target.value);
    setSelectedSectionId(sectionId);

    const coppieAssegnate = new Set(
      Object.values(classifiche)
        .flat()
        .map((entry) => entry.concorrente.id)
    );

    setAvailableCoppie(coppie.filter((g) => !coppieAssegnate.has(g.groupId)));
  };

  const handleAddToClassifica = async (groupId: number) => {
    if (!selectedSectionId) return;
    const coppia = availableCoppie.find((g) => g.groupId === groupId);
    if (coppia) {
      const membri = coppia.competitors
        .map((conc) => concorrenti.find((c) => c.id === conc.id))
        .filter(Boolean) as Concorrente[];
      // Nome completo accoppiato: "Cognome Nome & Cognome Nome ..."
      const nomiCompleti = membri.map((m) => `${m.lastName} ${m.firstName}`).join(" & ");
      const concorrenteFittizio: Concorrente = {
        id: coppia.groupId,
        firstName: nomiCompleti,
        lastName: "",
      } as Concorrente;
      setClassifiche((prev) => {
        const prevClassifica = prev[selectedSectionId] || [];
        return {
          ...prev,
          [selectedSectionId]: [
            ...prevClassifica,
            {
              concorrente: concorrenteFittizio,
              position: prevClassifica.length + 1,
              trote: 0,
              punteggio: 0,
            },
          ],
        };
      });
      setAvailableCoppie((prev) => prev.filter((g) => g.groupId !== groupId));
    }
  };

  const handleClassificaFieldChange = (
    sectionId: number,
    concorrenteId: number,
    field: "trote" | "punteggio",
    value: number
  ) => {
    setClassifiche((prev) => {
      const prevClassifica = prev[sectionId] || [];
      return {
        ...prev,
        [sectionId]: prevClassifica.map((entry) =>
          entry.concorrente.id === concorrenteId
            ? { ...entry, [field]: value }
            : entry
        ),
      };
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.addImage(logoBase64, "PNG", 10, 8, 24, 32);
    const garaName = competitionName || "Gara";
    const garaDate = competitionDate ? new Date(competitionDate) : null;
    const formattedDate = garaDate
      ? `${garaDate.getDate().toString().padStart(2, "0")}-${(
          garaDate.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${garaDate.getFullYear()}`
      : "";
    doc.setFontSize(16);
    doc.text(
      `${garaName}${formattedDate ? " (" + formattedDate + ")" : ""}`,
      44,
      24
    );
    let y = 48;
    sections.forEach((section) => {
      doc.setFontSize(13);
      doc.text(section.name, 10, y);
      y += 6;
      const rows = (classifiche[section.id] || []).map((entry) => [
        entry.concorrente.firstName,
        entry.punteggio,
        entry.trote,
        entry.position,
      ]);
      const totaleTrote = (classifiche[section.id] || []).reduce(
        (sum, entry) => sum + entry.trote,
        0
      );
      const footerRow = ["Totale trote settore", "", totaleTrote, ""];
      autoTable(doc, {
        startY: y,
        head: [["Nome e Cognome", "Punti", "Trote", "Posizione"]],
        body: rows,
        foot: [footerRow],
        theme: "grid",
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        margin: { left: 10, right: 10 },
      });
      y = (doc as any).lastAutoTable?.finalY
        ? (doc as any).lastAutoTable.finalY + 10
        : y + 30;
    });
    doc.save(`${competitionName}.pdf`);
  };

  return (
    <div>
      <div className="mb-4">
        <label className="block mb-2 font-medium">Seleziona settore</label>
        <select
          className="w-full border border-gray-300 rounded-md px-3 py-2"
          value={selectedSectionId ?? ""}
          onChange={handleSectionChange}
        >
          <option value="">Settore</option>
          {sections.map((section) => (
            <option key={section.id} value={section.id}>
              {section.name}
            </option>
          ))}
        </select>
      </div>
      {sections.map((section) => (
        <div key={section.id} className="mb-8">
          {selectedSectionId === section.id && (
            <div className="mb-4">
              <label className="block mb-2 font-medium">
                Aggiungi gruppo in classifica
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value=""
                onChange={(e) => handleAddToClassifica(Number(e.target.value))}
                disabled={availableCoppie.length === 0}
              >
                <option value="">Gruppo</option>
                {availableCoppie.map((g) => (
                  <option key={g.groupId} value={g.groupId}>
                    {g.competitors
                      .map((m) => `${m.lastName} ${m.firstName}`)
                      .join(" & ")}
                  </option>
                ))}
              </select>
            </div>
          )}
          <h4 className="font-semibold mb-2">Classifica ({section.name})</h4>
          <div className="hidden sm:flex flex-row flex-wrap items-center gap-2 w-full sm:w-auto font-semibold text-gray-700 mb-2 px-1 text-xs sm:text-base overflow-x-auto">
            <span className="w-6 flex-shrink-0"></span>
            <span className="flex-1 min-w-0">Concorrente</span>
            <span className="w-20 text-center">Trote</span>
            <span className="w-24 text-center">Punti</span>
            <span className="w-8"></span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (active.id !== over?.id) {
                setClassifiche((prev) => {
                  const prevClassifica = prev[section.id] || [];
                  const oldIndex = prevClassifica.findIndex(
                    (e) => e.concorrente.id === active.id
                  );
                  const newIndex = prevClassifica.findIndex(
                    (e) => e.concorrente.id === over?.id
                  );
                  const newArr = arrayMove(
                    prevClassifica,
                    oldIndex,
                    newIndex
                  ).map((entry: ClassificaEntry, idx: number) => ({
                    ...entry,
                    position: idx + 1,
                  }));
                  return {
                    ...prev,
                    [section.id]: newArr,
                  };
                });
              }
            }}
          >
            <SortableContext
              items={(classifiche[section.id] || []).map(
                (entry) => entry.concorrente.id
              )}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {(classifiche[section.id] || []).map((entry) => (
                  <SortableClassificaCoppiaItem
                    key={entry.concorrente.id}
                    id={entry.concorrente.id}
                    entry={entry}
                    sectionId={section.id}
                    onFieldChange={handleClassificaFieldChange}
                    onRemove={handleRemoveFromClassifica}
                    coppie={coppie}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      ))}
      <div className="flex gap-2 mb-2">
        <button
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg"
          onClick={handleSaveAllRankings}
          disabled={
            saving ||
            Object.values(classifiche).every((arr) => !arr || arr.length === 0)
          }
        >
          {saving ? "Salvataggio..." : "Salva tutte le classifiche"}
        </button>
        <button
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg"
          onClick={handleExportPDF}
          disabled={Object.values(classifiche).every(
            (arr) => !arr || arr.length === 0
          )}
        >
          Esporta PDF
        </button>
      </div>
      {saveMessage && (
        <div
          className={`mt-2 text-sm ${
            saveMessage.includes("successo") ? "text-green-600" : "text-red-600"
          }`}
        >
          {saveMessage}
        </div>
      )}
    </div>
  );
};

interface SortableClassificaItemProps {
  id: number;
  entry: ClassificaEntry;
  sectionId: number;
  onFieldChange: (
    sectionId: number,
    concorrenteId: number,
    field: "trote" | "punteggio",
    value: number
  ) => void;
  onRemove: (sectionId: number, concorrenteId: number) => void;
}

interface SortableClassificaCoppiaItemProps
  extends SortableClassificaItemProps {
  coppie: Coppia[];
}

function SortableClassificaCoppiaItem({
  id,
  entry,
  sectionId,
  onFieldChange,
  onRemove,
  coppie,
}: SortableClassificaCoppiaItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    background: isDragging ? "#f3f4f6" : undefined,
  };
  const coppia = coppie.find((c) => c.groupId === entry.concorrente.id);
  const nomiCoppia = coppia
    ? coppia.competitors.map((m) => `${m.lastName} ${m.firstName}`).join(" & ")
    : `${entry.concorrente.firstName} ${entry.concorrente.lastName}`;
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="px-1 py-1 text-xs sm:text-base"
    >
      <div className="flex flex-col gap-1 sm:hidden">
        <div className="flex flex-row items-center w-full gap-2">
          <div
            className="flex flex-row items-center flex-1 gap-2 cursor-move"
            {...attributes}
            {...listeners}
          >
            <span className="font-bold w-6 flex-shrink-0">
              {entry.position}
            </span>
            <span className="flex-1 min-w-0 truncate">{nomiCoppia}</span>
          </div>
          <button
            type="button"
            className="ml-2 text-red-600 hover:text-red-800"
            title="Rimuovi coppia dalla classifica"
            onClick={() => onRemove(sectionId, entry.concorrente.id)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex flex-row items-center gap-2 w-full">
          <label className="w-14 text-gray-600">trote</label>
          <input
            type="number"
            min={0}
            className="w-16 border border-gray-300 rounded px-1 py-1 text-center"
            value={entry.trote}
            onChange={(e) =>
              onFieldChange(
                sectionId,
                entry.concorrente.id,
                "trote",
                Number(e.target.value)
              )
            }
            placeholder="Trote"
            title="Trote pescate"
          />
        </div>
        <div className="flex flex-row items-center gap-2 w-full">
          <label className="w-14 text-gray-600">punti</label>
          <input
            type="number"
            min={0}
            className="w-16 border border-gray-300 rounded px-1 py-1 text-center"
            value={entry.punteggio}
            onChange={(e) =>
              onFieldChange(
                sectionId,
                entry.concorrente.id,
                "punteggio",
                Number(e.target.value)
              )
            }
            placeholder="Punteggio"
            title="Punteggio"
          />
        </div>
      </div>
      <div className="hidden sm:flex flex-row items-center gap-2 w-full">
        <div
          className="flex flex-row items-center flex-1 gap-2 cursor-move"
          {...attributes}
          {...listeners}
        >
          <span className="font-bold w-6 flex-shrink-0">{entry.position}</span>
          <span className="flex-1 min-w-0 truncate">{nomiCoppia}</span>
        </div>
        <input
          type="number"
          min={0}
          className="w-20 border border-gray-300 rounded px-2 py-1 text-center"
          value={entry.trote}
          onChange={(e) =>
            onFieldChange(
              sectionId,
              entry.concorrente.id,
              "trote",
              Number(e.target.value)
            )
          }
          placeholder="Trote"
          title="Trote pescate"
        />
        <input
          type="number"
          min={0}
          className="w-24 border border-gray-300 rounded px-2 py-1 text-center"
          value={entry.punteggio}
          onChange={(e) =>
            onFieldChange(
              sectionId,
              entry.concorrente.id,
              "punteggio",
              Number(e.target.value)
            )
          }
          placeholder="Punteggio"
          title="Punteggio"
        />
        <button
          type="button"
          className="ml-2 text-red-600 hover:text-red-800"
          title="Rimuovi coppia dalla classifica"
          onClick={() => onRemove(sectionId, entry.concorrente.id)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </li>
  );
}

export default ClassificaCoppie;
